import {
  Component,
  DestroyRef,
  Injector,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  catchError,
  distinctUntilChanged,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { AuthService } from '@/core/services/auth/auth.service';
import { TranslocoPageRouteSyncService } from '@/core/transloco/transloco-loader';
import { SubscriptionsService } from '@/services/subscriptions/subscriptions.service';
import {
  collectOverdraftRows,
  dismissSubPop,
  hasUsageOverdraft,
  isSubPopDismissed,
  UsageMetricLabels,
} from '@/services/subscriptions/subscription-usage.util';
import { SubscriptionOverdraftListTableService } from '@/services/subscriptions/subscription-overdraft.list.table.service';
import { SubscriptionOverdraftTableRow } from '@/services/subscriptions/subscription-overdraft.list.types';
import { BaseModalComponent } from '@/shared/components/base-modal/base-modal.component';
import { BaseTableComponent } from '@/shared/components/base-table/base-table.component';

@Component({
  selector: 'app-subscription-alert-modal',
  standalone: true,
  imports: [BaseModalComponent, BaseTableComponent, TranslocoPipe],
  providers: [SubscriptionOverdraftListTableService],
  templateUrl: './subscription-alert-modal.component.html',
  styleUrl: './subscription-alert-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SubscriptionAlertModalComponent {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private readonly pageI18n = inject(TranslocoPageRouteSyncService);
  private readonly subscriptionService = inject(SubscriptionsService);
  protected readonly overdraftTableService = inject(
    SubscriptionOverdraftListTableService
  );

  private overdraftTableReady = false;
  private lastOverdraftTableSyncKey = '';

  private readonly dismissalVersion = signal(0);

  private readonly organizationId = computed(() => {
    this.authService.user();
    return this.authService.user()?.organizationId ?? null;
  });

  protected readonly dismissed = computed(() => {
    this.dismissalVersion();
    return isSubPopDismissed(this.organizationId());
  });

  private readonly isAuthenticated = computed(() => {
    this.authService.user();
    return this.authService.isAuthenticated;
  });

  protected readonly shouldLoadSubscriptionState = computed(() => {
    this.organizationId();
    return (
      !this.dismissed() &&
      this.isAuthenticated() &&
      this.organizationId() != null
    );
  });

  private readonly subscription = toSignal(
    toObservable(this.shouldLoadSubscriptionState, {
      injector: this.injector,
    }).pipe(
      startWith(this.shouldLoadSubscriptionState()),
      distinctUntilChanged(),
      switchMap(shouldLoad => {
        if (!shouldLoad) {
          return of(null);
        }
        return this.subscriptionService
          .checkSubscription()
          .pipe(catchError(() => of(null)));
      })
    ),
    { initialValue: null }
  );

  private readonly subscriptionUsage = toSignal(
    toObservable(this.shouldLoadSubscriptionState, {
      injector: this.injector,
    }).pipe(
      startWith(this.shouldLoadSubscriptionState()),
      distinctUntilChanged(),
      switchMap(shouldLoad => {
        if (!shouldLoad) {
          return of(null);
        }
        return this.subscriptionService
          .getUsage()
          .pipe(catchError(() => of(null)));
      })
    ),
    { initialValue: null }
  );

  private readonly isSubscriptionExpired = computed(() => {
    const sub = this.subscription();
    if (!sub) return false;

    const now = Date.now();
    const paidUntil = sub.subscription_paid_until;
    const endDate = sub.subscription_end_date;

    return (
      (paidUntil != null && paidUntil < now) ||
      (endDate != null && endDate < now)
    );
  });

  private readonly hasUsageOverdraftFlag = computed(() => {
    const usage = this.subscriptionUsage();
    return usage != null && hasUsageOverdraft(usage);
  });

  protected readonly opened = computed(
    () =>
      !this.dismissed() &&
      (this.isSubscriptionExpired() || this.hasUsageOverdraftFlag())
  );

  protected readonly title = computed(() =>
    this.transloco.translate(
      this.isSubscriptionExpired()
        ? 'components.subscription.expired.title'
        : 'components.subscription.limitReached.title'
    )
  );

  protected readonly description = computed(() =>
    this.transloco.translate(
      this.isSubscriptionExpired()
        ? 'components.subscription.expired.desc'
        : 'components.subscription.limitReached.desc'
    )
  );

  protected readonly viewUsageButtonLabel = computed(() =>
    this.transloco.translate('components.subscription.usageTable.viewUsage')
  );

  protected readonly dismissButtonLabel = computed(() =>
    this.transloco.translate('components.subscription.dismiss')
  );

  protected readonly overdraftRows = computed(() => {
    const usage = this.subscriptionUsage();
    if (!usage) {
      return [];
    }
    return collectOverdraftRows(usage, this.usageMetricLabels());
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(
        startWith(this.transloco.getActiveLang()),
        switchMap(() => this.pageI18n.ensureKnownComponentBundle('subscription')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    effect(() => {
      if (!this.opened()) {
        return;
      }
      const rows = this.overdraftRows();
      untracked(() => this.syncOverdraftTable(rows));
    });
  }

  private syncOverdraftTable(
    rows: ReturnType<typeof collectOverdraftRows>
  ): void {
    const syncKey = rows
      .map(
        r => `${r.label}|${r.usedLabel}|${r.planLabel}|${r.overdraftCostLabel}`
      )
      .join(';');

    if (
      syncKey === this.lastOverdraftTableSyncKey &&
      this.overdraftTableReady
    ) {
      return;
    }

    this.lastOverdraftTableSyncKey = syncKey;

    if (!this.overdraftTableReady) {
      this.overdraftTableService.initialize(this.getOverdraftTableHeaders());
      this.overdraftTableReady = true;
    }

    this.overdraftTableService.setRows(this.mapOverdraftTableRows(rows));
  }

  closeOverlay = (): void => {
    dismissSubPop(this.organizationId());
    this.dismissalVersion.update(version => version + 1);
  };

  viewSubscriptionUsage = (): void => {
    void this.router.navigate(['/organization/control-panel/organization']);
    this.closeOverlay();
  };

  private usageMetricLabels(): UsageMetricLabels {
    const prefix = 'components.subscription.usageTable.metrics';
    return {
      traceability: this.transloco.translate(`${prefix}.traceability`),
      extended: this.transloco.translate(`${prefix}.extended`),
      engage: this.transloco.translate(`${prefix}.engage`),
      registeredUsers: this.transloco.translate(`${prefix}.registeredUsers`),
      loggedInUsers: this.transloco.translate(`${prefix}.loggedInUsers`),
      database: this.transloco.translate(`${prefix}.database`),
      publicStorage: this.transloco.translate(`${prefix}.publicStorage`),
      privateStorage: this.transloco.translate(`${prefix}.privateStorage`),
    };
  }

  private getOverdraftTableHeaders() {
    const prefix = 'components.subscription.usageTable';
    return {
      metric: this.transloco.translate(`${prefix}.metric`),
      currentUsage: this.transloco.translate(`${prefix}.currentUsage`),
      planLimit: this.transloco.translate(`${prefix}.planLimit`),
      overdraftCost: this.transloco.translate(`${prefix}.overdraftCost`),
    };
  }

  private mapOverdraftTableRows(
    rows: ReturnType<typeof collectOverdraftRows>
  ): SubscriptionOverdraftTableRow[] {
    return rows.map((row, index) => ({
      id: String(index),
      metric: row.label,
      current_usage: row.usedLabel,
      plan_limit: row.planLabel,
      overdraft_cost: row.overdraftCostLabel,
    }));
  }
}
